import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { getService, SERVICES, SOCIALS } from "../data/services";
import { MaskLine, Reveal } from "../components/Reveal";
import BookingForm from "../components/BookingForm";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) return <Navigate to="/" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <main className="pt-[72px]" data-testid="service-detail-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/30" />
        </div>

        <div className="max-w-[1400px] mx-auto w-full px-5 sm:px-8 py-16">
          <Link
            to={service.brand === "studios" ? "/studios" : "/collectibles"}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 hover:text-gold mb-10 transition-colors"
            data-testid="detail-back"
          >
            <ArrowLeft size={15} /> {service.brand === "studios" ? "SEC Studios" : "All Card Services"}
          </Link>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-5">
            {service.number} / {service.brandLabel} / {service.category}
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-semibold uppercase tracking-tighter leading-[0.88]">
            <MaskLine>{service.title}</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="text-gold text-lg lg:text-xl mt-6 max-w-2xl"
          >
            {service.tagline}
          </motion.p>
        </div>
      </section>

      {/* Intro + pricing */}
      <section className="py-20 sm:py-28 border-t border-[#27272a]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
                [ Overview ]
              </span>
              <p className="text-zinc-300 text-lg leading-relaxed mt-5">
                {service.intro}
              </p>
              {service.priceHeadline && (
                <div className="mt-8 border border-gold/40 bg-gold/5 p-6">
                  <div className="font-display text-5xl font-semibold text-gold">
                    {service.priceHeadline}
                  </div>
                  <p className="text-zinc-400 text-sm mt-2">{service.priceSub}</p>
                </div>
              )}
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
                [ Pricing Tiers ]
              </span>
              <div className="mt-6 border border-[#27272a]">
                {service.tiers.map((t, i) => (
                  <div
                    key={i}
                    className={`grid sm:grid-cols-12 gap-3 p-6 border-b border-[#27272a] last:border-b-0 ${
                      t.highlight ? "bg-gold/5" : ""
                    }`}
                    data-testid={`tier-${i}`}
                  >
                    <div className="sm:col-span-8">
                      <h3 className="font-display text-lg font-semibold flex items-center gap-3">
                        {t.name}
                        {t.highlight && (
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-black bg-gold px-2 py-0.5">
                            Popular
                          </span>
                        )}
                      </h3>
                      <p className="text-zinc-400 text-sm mt-2">{t.desc}</p>
                    </div>
                    <div className="sm:col-span-4 sm:text-right">
                      <span className="font-mono text-xl text-gold">{t.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 sm:py-28 border-t border-[#27272a]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold uppercase tracking-tight mb-12">
            What's Included
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {service.features.map((f, i) => (
              <Reveal key={i} delay={(i % 2) * 0.1}>
                <div className="border border-[#27272a] p-8 h-full hover:border-gold/40 transition-colors duration-500">
                  <Check className="text-gold mb-4" size={22} />
                  <h3 className="font-display text-xl font-semibold mb-3">
                    {f.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed text-sm">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <section className="py-20 sm:py-28 border-t border-[#27272a]" id="detail-book">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tighter leading-[0.9]">
                {service.cta}
              </h2>
              <p className="text-zinc-400 mt-5">
                Ready to go? Send your details and we'll get back to you fast.
              </p>
              <span className="inline-block mt-4 font-mono text-xs text-zinc-500 tracking-[0.15em]">
                {service.brand === "studios" ? SOCIALS.studios : SOCIALS.collectibles}
              </span>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <BookingForm defaultService={service.title} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="py-20 border-t border-[#27272a]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
            [ Keep Exploring ]
          </span>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group border border-[#27272a] p-7 hover:border-gold/40 hover:bg-[#111] transition-all duration-300"
                data-testid={`related-${s.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-zinc-500">{s.number}</span>
                  <ArrowUpRight
                    size={18}
                    className="text-zinc-600 group-hover:text-gold transition-colors"
                  />
                </div>
                <h3 className="font-display text-xl font-semibold mt-8 group-hover:text-gold transition-colors">
                  {s.title}
                </h3>
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                  {s.brandLabel}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
